import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChatPanel } from "@/components/ChatPanel";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";

// -----------------------------------------------------------------------------
// Guest chat resumption — localStorage schema (single canonical key per vehicle)
//
//   Key:   belomotors_guest_thread_<vehicle_id>
//   Value: { thread_id, guest_token, guest_email, updated_at }
//
// One thread per vehicle per guest is fine for MVP. Submitting a new lead on the
// same vehicle later overwrites the record so the newest thread wins.
// -----------------------------------------------------------------------------
const GUEST_THREAD_KEY = (vehicleId) => `belomotors_guest_thread_${vehicleId}`;

export const saveGuestChat = ({ threadId, token, email, vehicleId }) => {
  if (!vehicleId || !threadId || !token) return;
  try {
    localStorage.setItem(
      GUEST_THREAD_KEY(vehicleId),
      JSON.stringify({
        thread_id: threadId,
        guest_token: token,
        guest_email: email || null,
        updated_at: new Date().toISOString(),
      })
    );
  } catch {
    // storage unavailable — silently skip
  }
};

export const loadGuestChatForVehicle = (vehicleId) => {
  if (!vehicleId) return null;
  try {
    const raw = localStorage.getItem(GUEST_THREAD_KEY(vehicleId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.thread_id || !parsed?.guest_token) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const clearGuestChatForVehicle = (vehicleId) => {
  if (!vehicleId) return;
  try { localStorage.removeItem(GUEST_THREAD_KEY(vehicleId)); } catch { /* noop */ }
};

/**
 * Floating chat drawer for customers and guests.
 *
 * Modes:
 *  - Auth'd customer: pass `threadId` (customer JWT auto-attached by api client)
 *  - Guest:           pass `guestToken` (JWT tied to thread_id + email)
 *
 * Props:
 *  - open / onOpenChange (optional): fully-controlled drawer. When omitted the
 *    component manages its own state and shows the floating FAB.
 *  - defaultOpen: initial open state when uncontrolled.
 *  - trigger: optional React element used instead of the floating FAB.
 *
 * If neither threadId nor guestToken is passed the drawer renders nothing.
 */
export const ChatDrawer = ({
  threadId,
  guestToken,
  defaultOpen = false,
  trigger,
  open: controlledOpen,
  onOpenChange,
  showFab = true,
}) => {
  const { user } = useAuth();
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const isControlled = typeof controlledOpen === "boolean";
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v);
    else setInternalOpen(v);
  };

  useEffect(() => { if (!isControlled) setInternalOpen(defaultOpen); }, [defaultOpen, isControlled]);

  // messagesEndpoint MUST be stable across renders so ChatPanel's polling and
  // initial-fetch effects don't thrash on every parent re-render. Stability is
  // achieved by depending only on the primitive identifiers.
  const mode = threadId && user?.role === "customer" ? "customer" : (guestToken ? "guest" : null);

  const messagesEndpoint = useCallback(async (after) => {
    if (mode === "customer") {
      if (!after) {
        // Initial fetch returns the full thread + all messages.
        const r = await api.get(`/me/threads/${threadId}`);
        return { data: { messages: r.data?.messages || [], server_time: r.data?.thread?.updated_at } };
      }
      return api.get(`/me/threads/${threadId}/messages`, { params: { after } });
    }
    if (mode === "guest") {
      if (!after) {
        const r = await api.get("/threads/guest", { params: { token: guestToken } });
        return { data: { messages: r.data?.messages || [], server_time: r.data?.thread?.updated_at } };
      }
      return api.get("/threads/guest/messages", { params: { token: guestToken, after } });
    }
    return { data: { messages: [] } };
  }, [mode, threadId, guestToken]);

  const sendEndpoint = useCallback(async (body) => {
    if (mode === "customer") return api.post(`/me/threads/${threadId}/messages`, { body });
    if (mode === "guest") return api.post("/threads/guest/messages", { body }, { params: { token: guestToken } });
    throw new Error("Chat drawer has no valid mode configured");
  }, [mode, threadId, guestToken]);

  // Key changes when we swap modes/threads so ChatPanel remounts cleanly and
  // starts a fresh initial fetch — this is what guarantees the return-visit
  // "Open chat" click always sees history, never a stale empty panel.
  const panelKey = useMemo(() => `${mode || "none"}:${threadId || guestToken || "empty"}`, [mode, threadId, guestToken]);

  if (!mode) return null;

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, { onClick: () => setOpen(true) })
      ) : showFab && !isControlled ? (
        <Button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-30 rounded-full h-14 w-14 shadow-xl bg-brand hover:bg-brand-hover text-white p-0"
          data-testid="chat-drawer-fab"
          aria-label="Open chat"
        >
          <MessageCircle size={22} />
        </Button>
      ) : null}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="chat-drawer">
          <SheetTitle className="sr-only">Belomotors chat with your BDC concierge</SheetTitle>
          <SheetDescription className="sr-only">
            Live chat with a Belomotors BDC agent about your appointment request.
          </SheetDescription>
          <div className="px-4 py-3 border-b border-slate-200 bg-brand text-white flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">Belomotors chat</p>
              <p className="text-sm font-semibold">Talk to your BDC concierge</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              aria-label="Close"
              data-testid="chat-drawer-close-btn"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 p-3 min-h-0">
            <ChatPanel
              key={panelKey}
              viewerType="customer"
              messagesEndpoint={messagesEndpoint}
              sendEndpoint={sendEndpoint}
              emptyStateText="Send us a note — we'll follow up within business hours."
              testIdPrefix="customer-chat"
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export { GUEST_THREAD_KEY };
